import React, {
  useMemo, useRef, useState,
} from "react"
import _ from "lodash"
import Icon from "components/elements/Icon"
import GuestListIcon from "components/elements/GuestListIcon"
import { ModalName } from "sharedTypes"
import {
  Box, Flex, Text,
} from "@chakra-ui/react"
import { useCurrentEvent } from "queries/events"
import useModal from "services/useModal"
import { useDeepCompareEffect } from "ahooks"
import { Combobox } from "components/Combobox"

type EventGroupRef = { id: number; title: string; icon: string|null; color?: string|null }

// Module-level stable reference. Without this, `currentEvent.eventGroups || []`
// allocates a fresh empty array on every render, busting referential equality
// checks downstream (useMemo deps, optimistic-state diff).
const NO_GROUPS: EventGroupRef[] = []

type Props = {
  eventGroups: EventGroupRef[]
  updateGuest: (params: {}) => Promise<unknown>
  disabled: boolean
}

const GuestLists = ({ eventGroups, updateGuest, disabled }: Props) => {
  const { data: currentEvent } = useCurrentEvent()
  const showModal = useModal()

  // API returns event_groups already sorted by `lower(title) ASC`.
  const allGroups = currentEvent.eventGroups ?? NO_GROUPS

  // Optimistic local state: drives the UI immediately on toggle, so the
  // checkboxes flip and the trigger badges update without waiting for the
  // debounced API call + react-query refetch (which combined felt like ~1s).
  //
  // The tricky bit is when a user toggles twice quickly. A naive "pending"
  // flag gets cleared by the first response, which then lets the first
  // response's stale prop update overwrite the user's second click, only for
  // the second response to flip it back — a visible revert-then-correct
  // flicker.
  //
  // Instead we track the *expected* prop value: what we last sent. The
  // prop→local sync only runs when the incoming prop matches what we expect,
  // i.e. the server has caught up with our latest local intent. Earlier
  // intermediate responses are ignored.
  const [optimistic, setOptimistic] = useState<EventGroupRef[]>(eventGroups)
  const expectedRef = useRef<EventGroupRef[]>(eventGroups)

  const updateGuestRef = useRef(updateGuest)
  updateGuestRef.current = updateGuest
  const debouncedUpdate = useMemo(
    () => _.debounce(
      (groups: EventGroupRef[]) => updateGuestRef.current({ eventGroups: groups }),
      500,
    ),
    [],
  )

  useDeepCompareEffect(() => {
    const propIds = new Set(eventGroups.map((g) => g.id))
    const expectedIds = new Set(expectedRef.current.map((g) => g.id))
    const matches = propIds.size === expectedIds.size
      && [...propIds].every((id) => expectedIds.has(id))
    if (matches) {
      // Server caught up with our latest intent (or we're at initial state
      // with no local changes). Safe to adopt the prop.
      setOptimistic(eventGroups)
    }
    // If doesn't match: a newer local change is in flight, hold optimistic.
  }, [eventGroups])

  const selected = useMemo(() => {
    const ids = new Set(optimistic.map((g) => g.id))

    return allGroups.filter((g) => ids.has(g.id))
  }, [allGroups, optimistic])

  const handleChange = (newSelected: EventGroupRef[]) => {
    setOptimistic(newSelected)
    expectedRef.current = newSelected
    debouncedUpdate(newSelected)
  }

  const handleCreate = (title: string) => {
    debouncedUpdate.flush()
    showModal(ModalName.GuestListForm, { defaultTitle: title })
  }

  // Trigger badges read from optimistic state too — instant update on toggle.
  const triggerSelections = useMemo(
    () => _.sortBy(optimistic, (g) => g.title.toLowerCase()),
    [optimistic],
  )

  const triggerContent = triggerSelections.length > 0
    ? (
      triggerSelections.map(({
        id, title, icon, color,
      }) => (
        <Flex
          align="center"
          mb={3}
          mx={4}
          bg="none"
          color="gray.500"
          fontSize="lg"
          key={id}
        >
          <GuestListIcon icon={icon} color={color} mr={2} /> {title}
        </Flex>
      ))
    )
    : (
      <Flex align="center" mb={3} color="gray.500" fontSize="lg">
        <Icon icon="tag" mr={2} /> No Guest List Selected
      </Flex>
    )

  return (
    <Combobox.Root<EventGroupRef>
      items={allGroups}
      getItemKey={(g) => g.id.toString()}
      getItemLabel={(g) => g.title}
      multiple
      value={selected}
      onChange={handleChange}
      onCreate={handleCreate}
    >
      <Combobox.Trigger>
        <Flex
          borderWidth={1}
          borderRadius="base"
          position="relative"
          align="center"
          justify="center"
          wrap="wrap"
          mt={2}
          px={4}
          pt={4}
          pb={1}
          pointerEvents={disabled ? "none" : "auto"}
          opacity={disabled ? 0.6 : 1}
        >
          {triggerContent}
          {!disabled && (
            <Box position="absolute" right={0} mb={3}>
              <Icon icon="down-arrow" size={3} mr={2} color="gray.300" />
            </Box>
          )}
        </Flex>
      </Combobox.Trigger>
      <Combobox.Content w="320px">
        <Combobox.Header>Guest List</Combobox.Header>
        <Combobox.Input placeholder="Search Guest Lists" />
        <Combobox.List<EventGroupRef> maxH="280px">
          {(g) => (
            <Combobox.Item item={g}>
              <GuestListIcon icon={g.icon} color={g.color} mr={2} />
              <Text>{g.title}</Text>
            </Combobox.Item>
          )}
        </Combobox.List>
        <Combobox.Create>
          {(query) => (
            <Flex align="center">
              <Icon icon="plus-big" mr={2} />
              <Text>{query ? `New Guest List "${query}"` : "New Guest List"}</Text>
            </Flex>
          )}
        </Combobox.Create>
      </Combobox.Content>
    </Combobox.Root>
  )
}

export default GuestLists
